// The trader score on screen (traderScore.js does the math): a full-width bar for the Overview and
// Dashboard 3, and the full section for the Score dashboard tab and the top of Am I getting better?
// (ring, week by week, the six parts with their real numbers, and the one change that lifts it most).
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { addRoutineRule } from './routineRules';
import InfoTip from './InfoTip';
import { traderScore, MIN_DAYS, MIN_TRADES } from './traderScore';
import { usd } from './pnl';
import { currentLocale } from './langState';
import { useI18n } from './i18n';

const SCORE_PATH = '/app/journey/better#score';
const SCORE_INFO = 'One number from 0 to 100 for your last 30 trading days, made of six parts: profit factor, win size vs loss size, win rate, drawdown control, recovery and consistency. Around 40 or lower usually means losing money, 60 is steady and 80 or more is strong.';
const money = (n) => `${n < 0 ? '−' : ''}${usd.format(Math.abs(n || 0))}`;
const dayShort = (key) => new Date(`${key}T12:00:00`).toLocaleDateString(currentLocale(), { month: 'short', day: 'numeric' });
const monthLong = (m) => new Date(`${m}-15T12:00:00`).toLocaleDateString(currentLocale(), { month: 'long', year: 'numeric' });

export function useTraderScore(trades) {
  return useMemo(() => traderScore(trades), [trades]);
}

export function ScoreRing({ score, size = 64, stroke = 12 }) {
  const C = 2 * Math.PI * 48;
  return (
    <svg className="ts-ring" viewBox="0 0 120 120" style={{ width: size, height: size }} role="img" aria-label={`${score} / 100`}>
      <circle cx="60" cy="60" r="48" className="track" strokeWidth={stroke} />
      <circle cx="60" cy="60" r="48" className="on" strokeWidth={stroke} strokeDasharray={`${(score / 100) * C} ${C}`} transform="rotate(-90 60 60)" />
      <text x="60" y="73" textAnchor="middle">{score}</text>
    </svg>
  );
}

// The lowest part glows gold; strong parts are green, the rest cyan.
const partTone = (p, lowest) => (p.id === lowest.id ? 'gold' : p.score >= 65 ? 'gain' : 'cyan');
const lowestOf = (parts) => parts.reduce((lo, p) => (p.score < lo.score ? p : lo), parts[0]);

function Change({ data }) {
  const { t } = useI18n();
  if (!data.lastMonth) return null;
  const d = data.now.score - data.lastMonth.score;
  return <span className={d > 0 ? 'gain' : d < 0 ? 'loss' : 'dov-muted'}>{d > 0 ? '▲' : d < 0 ? '▼' : '='} {Math.abs(d)}<small className="dov-muted"> {t('vs last month')}</small></span>;
}

function NotYet({ data }) {
  const { t } = useI18n();
  return <span className="dov-muted">{t('Your trader score appears after {t} trades over {d} trading days.', { t: MIN_TRADES, d: MIN_DAYS })}{data && data.need ? ` ${t('{n} more to go.', { n: Math.max(data.need.trades, data.need.days) })}` : ''}</span>;
}

// Overview and Dashboard 3: one bar across the page.
export function ScoreBar({ trades }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const data = useTraderScore(trades);
  if (!data || !data.now) return <section className="dov-card ts-bar ts-empty"><span className="dov-label">{t('Trader score')}</span><NotYet data={data} /></section>;
  const { now } = data;
  const lowest = lowestOf(now.parts);
  return (
    <section className="dov-card ts-bar">
      <ScoreRing score={now.score} size={70} />
      <span className="ts-bar-head">
        <span className="dov-label">{t('Trader score')}<InfoTip label={t('Trader score')} text={t(SCORE_INFO)} /></span>
        <b>{t(now.level)} <Change data={data} /></b>
        <small className="dov-muted">{t('Last 30 trading days')}{data.best ? ` · ${t('best {n}', { n: data.best.score })}` : ''}</small>
      </span>
      <span className="ts-bar-parts">
        {now.parts.map((p) => {
          const e = explain(p, now.stats, t);
          return (
            <span key={p.id} className={`ts-mini ts-stack ${partTone(p, lowest)}`} title={`${t(p.label)}: ${e.big}`}>
              <span className="ts-stack-label"><span>{t(p.short || p.label)}</span><small>{p.score}</small></span>
              <b className={`ts-stack-value ${e.tone}`}>{p.id === 'drawdown' && now.stats.maxDD >= 1000 ? `−$${(now.stats.maxDD / 1000).toFixed(1)}K` : e.big}</b>
              <span className="ts-track"><i style={{ width: `${p.score}%` }} /></span>
            </span>
          );
        })}
      </span>
      <button type="button" className="link-button" onClick={() => navigate(SCORE_PATH)}>{t('See my score')} &rarr;</button>
    </section>
  );
}

// Each part's real number (big, colored good or bad), what it is, one sentence, and its info mark.
function explain(p, s, t) {
  const good = (ok) => (ok ? 'gain' : 'loss');
  switch (p.id) {
    case 'pf': return {
      big: s.pf === Infinity ? '∞' : s.pf.toFixed(2), tone: good(s.pf >= 1), sub: t('profit factor'),
      line: s.pf === Infinity ? t('No losing trades in this stretch.') : t('Earning {x} for every $1 lost.', { x: `$${s.pf.toFixed(2)}` }),
      info: t('What your winners made divided by what your losers lost. Above 1.00 you make money; 1.50 or more is strong. This part is 20% of your score.'),
    };
    case 'ratio': return {
      big: s.ratio === Infinity ? '∞' : `${s.ratio.toFixed(2)}×`, tone: good(s.ratio >= 1), sub: t('average win ÷ average loss'),
      line: t('Average win {w} vs average loss {l}.', { w: usd.format(s.avgWin), l: usd.format(s.avgLoss) }),
      info: t('Your average winning trade divided by your average losing trade. Above 1.00× your wins are bigger than your losses. This part is 15% of your score.'),
    };
    case 'winRate': return {
      big: `${Math.round(s.winRate)}%`, tone: good(s.winRate >= s.breakEven), sub: t('of trades won'),
      line: t(s.winRate >= s.breakEven ? 'Above the {be}% you need to break even at your win size.' : 'Below the {be}% you need to break even at your win size.', { be: Math.round(s.breakEven) }),
      info: t('The share of your trades that made money, judged against the win rate you need to break even at your win size. A low win rate with big wins can still score well. This part is 15% of your score.'),
    };
    case 'drawdown': return {
      big: s.maxDD ? money(-s.maxDD) : '$0', tone: s.ddRatio <= 2 ? 'gain' : 'loss', sub: t('deepest drop from a high'),
      line: !s.maxDD ? t('No drop from a high in this stretch.') : s.ddRatio === Infinity ? t('Your deepest drop was {dd}, with no green days to balance it.', { dd: usd.format(s.maxDD) }) : t('That’s {x}× your average green day.', { x: s.ddRatio.toFixed(1) }),
      info: t('The biggest drop in your running P&L from a high point over these 30 days, compared with your average green day. Up to 2× your average green day scores well. This part is 20% of your score.'),
    };
    case 'recovery': return {
      big: !s.maxDD ? '—' : s.recovery === Infinity ? '∞' : `${s.recovery.toFixed(1)}×`, tone: !s.maxDD ? '' : good(s.recovery >= 1), sub: t('net P&L ÷ deepest drop'),
      line: !s.maxDD ? t('Nothing to recover from in this stretch.') : s.net > 0 ? t('You made back {x}× your deepest drop.', { x: s.recovery.toFixed(1) }) : t('Still below where this stretch started: net {net}.', { net: money(s.net) }),
      info: t('Your net P&L for these 30 days divided by your deepest drop: how well you climb back after a bad stretch. Above 1× you made back more than that drop. This part is 15% of your score.'),
    };
    default: return {
      big: `${Math.round(s.greenPct)}%`, tone: good(s.greenPct >= 50), sub: t('green days'),
      line: `${t('{g} of {d} days green', { g: s.greenDays, d: s.days })}${s.bestShare !== null ? `; ${t('best day is {p}% of profit', { p: Math.round(s.bestShare) })}` : ''}.`,
      info: t('How many of your trading days ended green, and whether one big day carries most of the profit (40% or more is a warning sign). This part is 15% of your score.'),
    };
  }
}

function Trend({ weeks }) {
  const { t } = useI18n();
  const W = 700;
  const H = 180;
  const y = (v) => H - (v / 100) * H;
  const x = (i) => (i / Math.max(1, weeks.length - 1)) * W;
  const pts = weeks.map((w, i) => `${x(i).toFixed(1)},${y(w.score).toFixed(1)}`).join(' ');
  const labels = weeks.length < 2 ? weeks : [0, 1, 2, 3].map((i) => weeks[Math.round((i / 3) * (weeks.length - 1))]);
  return (
    <section className="dov-card ts-trend">
      <div className="dov-row"><span className="dov-label">{t('Your score week by week')}<InfoTip label={t('Your score week by week')} text={t('Your score at the end of each week, worked out on the 30 trading days up to that week. A rising line means you’re getting better.')} /></span><small className="dov-muted">{t('40 = losing money · 60 = steady · 80+ = strong')}</small></div>
      {weeks.length > 1 ? (
        <>
          <div className="ts-trend-plot">
            <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" role="img" aria-label={t('Your score week by week')}>
              <rect x="0" y="0" width={W} height={y(80)} className="band-good" />
              <rect x="0" y={y(40)} width={W} height={H - y(40)} className="band-low" />
              <line x1="0" x2={W} y1={y(60)} y2={y(60)} className="grid" />
              <line x1="0" x2={W} y1={y(40)} y2={y(40)} className="grid" />
              <polyline points={pts} className="line" />
            </svg>
            <span className="ts-trend-y" style={{ top: `${(y(60) / H) * 100}%` }}>60</span>
            <span className="ts-trend-y" style={{ top: `${(y(40) / H) * 100}%` }}>40</span>
            <span className="ts-trend-end" style={{ top: `${(y(weeks[weeks.length - 1].score) / H) * 100}%` }} />
          </div>
          <span className="d3-axis">{labels.map((w, i) => <small key={`${w.week}${i}`}>{dayShort(w.end)}</small>)}</span>
        </>
      ) : <p className="dov-muted">{t('The line fills in week by week.')}</p>}
    </section>
  );
}

// Am I getting better?: the whole score.
// The heading on one line, for the Score dashboard's tab row.
export function ScoreHeading({ data }) {
  const { t } = useI18n();
  const s = data.now.stats;
  return (
    <span className="ts-inline-head">
      <h2 className="ts-h">{t('My trader score')}</h2>
      <small className="dov-muted">{t('{from} – {to} · {n} trades', { from: dayShort(s.from), to: dayShort(s.to), n: s.trades })}</small>
    </span>
  );
}

// Am I getting better? and the Score dashboard: the whole score. On the Score dashboard the heading
// sits in the tab row (headMoved), so the score lines up with the accounts beside it.
export function TraderScoreSection({ trades, data: given, headMoved = false }) {
  const { t } = useI18n();
  const computed = useMemo(() => (given === undefined ? traderScore(trades) : null), [given, trades]);
  const data = given === undefined ? computed : given;
  const [added, setAdded] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (window.location.hash === '#score') {
      const el = document.getElementById('score');
      if (el) el.scrollIntoView({ block: 'start' });
    }
  }, []);
  if (!data || !data.now) {
    return <section id="score" className="dov-card ts-section"><h2 className="ts-h">{t('My trader score')}</h2><NotYet data={data} /></section>;
  }
  const { now, move } = data;
  const s = now.stats;
  const lowest = lowestOf(now.parts);
  async function add() {
    setBusy(true);
    try {
      await addRoutineRule(move.rule);
      setAdded(true);
    } catch (err) {
      window.alert(err.message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <section id="score" className="dov ts-section">
      <div className={headMoved ? 'ts-head-moved' : ''}>
        <h2 className="ts-h">{t('My trader score')}</h2>
        <p className="dov-muted">{t('Last 30 trading days ({from} – {to}) · {n} trades · compared with your own history', { from: dayShort(s.from), to: dayShort(s.to), n: s.trades })}</p>
      </div>
      <div className="ts-top">
        <section className="dov-card ts-main">
          <span className="dov-label">{t('Trader score')}<InfoTip label={t('Trader score')} text={t(SCORE_INFO)} /></span>
          <ScoreRing score={now.score} size={190} stroke={10} />
          <span className="ts-level">{t(now.level)} · {now.score >= 80 ? '80–100' : now.score >= 60 ? '60–79' : now.score >= 40 ? '40–59' : '0–39'}</span>
          {data.lastMonth && <span><Change data={data} /> <small className="dov-muted">({data.lastMonth.score})</small></span>}
          {data.best && <small className="dov-muted">{t('Your best: {n} in {month}', { n: data.best.score, month: monthLong(data.best.month) })}</small>}
        </section>
        <Trend weeks={data.weeks} />
      </div>
      <div className="ts-parts">
        {now.parts.map((p) => {
          const e = explain(p, s, t);
          const tone = partTone(p, lowest);
          return (
            <section key={p.id} className={`dov-card ts-part${p.id === lowest.id ? ' lowest' : ''}`}>
              <span className="dov-row">
                <span className={`dov-label${p.id === lowest.id ? ' gold' : ''}`}>{t(p.label)}{p.id === lowest.id ? ` · ${t('lowest')}` : ''}<InfoTip label={t(p.label)} text={e.info} /></span>
                <span className="ts-weight">{Math.round(p.weight * 100)}%</span>
              </span>
              <span className="ts-value-row">
                <span className="ts-value"><b className={e.tone}>{e.big}</b><small className="dov-muted">{e.sub}</small></span>
                <span className={`ts-part-pill ${tone}`} title={t('This part’s score, out of 100')}>{t('Part score')} <b>{p.score}</b></span>
              </span>
              <span className={`ts-track big ${tone}`}><i style={{ width: `${p.score}%` }} /></span>
              <small className="dov-muted">{e.line}</small>
            </section>
          );
        })}
      </div>
      <div className="ts-bottom">
        <section className={`dov-card ts-move${move ? '' : ' quiet'}`}>
          <span className="dov-label gold">{t('What would move your score the most')}<InfoTip label={t('What would move your score the most')} text={t('We replay your last 30 trading days with three common rules (a daily loss stop, a daily trade limit, and stopping after 3 losses in a row) and show the one that lifts your score most. It’s worked out from your own trades.')} /></span>
          {move ? (
            <>
              <p className="ts-move-text">{move.text} → {t('about')} <span className="gold">{t('+{n} points', { n: move.gain })}</span> ({now.score} → {move.after.score})</p>
              <small className="dov-muted">{move.detail} {t('Replaying these 30 days with that rule lifts {part} from {a} to {b}.', { part: t(move.moved.label), a: move.moved.before, b: move.moved.score })}</small>
              <div className="dov-row d3-leak-actions">
                {added ? <span className="gain">✓ {t('Added to your rules')}</span>
                  : <button type="button" className="dov-gold small" disabled={busy} onClick={add} title={t('Adds “{rule}” to your rules in Morning prep.', { rule: move.rule })}>{t('Add as a rule')}</button>}
              </div>
            </>
          ) : <p className="dov-muted">{t('None of the usual rules (a daily loss stop, a trade limit, stopping after 3 losses in a row) would lift your score by 2 points or more right now. Keep doing what you’re doing.')}</p>}
        </section>
        <section className="dov-card ts-how">
          <span className="dov-label">{t('How the score works')}</span>
          <p className="dov-muted">{t('Six parts, each scored 0 to 100 from your last 30 trading days, then weighted: profit factor and drawdown control 20% each; win size, win rate, recovery and consistency 15% each. Win rate is judged against the win rate you need to break even at your win size, so a low win rate with big wins can still score well.')}</p>
        </section>
      </div>
    </section>
  );
}
